import { CSSProperties } from "react";

import { Calendar } from "lucide-react";
import { useNavigate } from "react-router";

import { BoxProps } from "@/components/layout/flexbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Button } from "@/components/ui/button";
import { Assignment } from "@/types/coaches";
import { CoachingCurrentUser, CoachingCustomer } from "@/types/users";

import { CoachMenu } from "./CoachMenu";

interface CoachProps {
  userId: string;
  assignment: Assignment;
  viewAsCoach: boolean;
  onRemove?: () => void;
  workspaceId: string | null;
}

export function Coach({
  userId,
  assignment,
  viewAsCoach,
  onRemove,
  workspaceId,
}: CoachProps) {
  const coach = assignment.coach;
  const coachSlug = assignment.coach.coachSlug;
  const navigate = useNavigate();

  const onPressCoach = () => {
    navigate(`/@${coachSlug}`);
  };

  let scheduleLink = `/schedule/${coach.id}`;
  if (viewAsCoach) {
    const workspaceIdParam = workspaceId ? workspaceId : "-1";
    scheduleLink = `/schedule/${coach.id}?clientId=${userId}&workspaceId=${workspaceIdParam}`;
  }

  const handleScheduleClick = () => {
    navigate(scheduleLink);
  };

  return (
    <div className="relative p-3 rounded-md bg-white border flex h-full flex-col justify-between">
      <Clickable onClick={onPressCoach}>
        <div className="flex flex-col items-center">
          <Avatar user={coach} size={75} />
          <div className="mt-2 font-semibold text-base md:text-lg text-center">
            {coach.name}
          </div>
        </div>
      </Clickable>
      <div className="mt-3 w-full">
        <Button
          onClick={handleScheduleClick}
          className="w-full"
          intent="secondary"
        >
          <Calendar />
          Schedule
        </Button>
      </div>

      <div className="absolute top-1 right-1">
        <CoachMenu
          userId={userId}
          coach={coach}
          assignment={assignment}
          chatChannelId={assignment.chatChannelId}
          viewAsCoach={viewAsCoach}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
}

interface YourCoachesProps extends BoxProps {
  user: CoachingCurrentUser | CoachingCustomer;
  assignments: Assignment[];
  viewAsCoach: boolean;
  workspaceId: string | null;
  style?: CSSProperties;
}

export function YourCoaches({
  user,
  assignments,
  viewAsCoach,
  workspaceId,
  style,
  ...props
}: YourCoachesProps) {
  return (
    <div className="bg-white p-3 md:p-6" style={style} {...props}>
      <div className="flex justify-between items-center">
        <div className="text-xl font-semibold">
          {viewAsCoach ? "Client Coaches" : "Your Coaches"}
        </div>
        {!viewAsCoach && (
          <div className="">
            <a href="/match">Browse Coaches</a>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-3">
        {assignments.map((assignment) => (
          <div key={assignment.id} className="min-w-[200px]">
            <Coach
              userId={user.id}
              assignment={assignment}
              viewAsCoach={viewAsCoach}
              workspaceId={workspaceId}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
